import React, { useState, useEffect, useRef } from "react";
import Container from "./ui/Container";
import { cn } from "@/lib/utils";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const testimonials = [
    {
      quote: "Since implementing TechOvation's automation solutions, we've cut our administrative work by 65% and our team can finally focus on strategic initiatives.",
      author: "Sarah Johnson",
      role: "Marketing Director, GrowthFirm",
      rating: 5,
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1587&q=80"
    },
    {
      quote: "Their Salesforce integration saved us countless hours of manual data entry. Our sales team now has 40% more time to actually talk to customers.",
      author: "David Chen",
      role: "Sales Manager, FutureTech",
      rating: 5,
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1587&q=80"
    },
    {
      quote: "The AI-powered customer service solution TechOvation built for us handles 80% of routine inquiries automatically. Our customer satisfaction scores have never been higher.",
      author: "Melissa Rodriguez",
      role: "Customer Service Director, ServicePro",
      rating: 5,
      image: "https://images.unsplash.com/photo-1509967419530-da38b4704bc6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1587&q=80"
    }
  ];

  const goToSlide = (index: number) => {
    let newIndex = index;
    if (newIndex < 0) {
      newIndex = testimonials.length - 1;
    } else if (newIndex >= testimonials.length) {
      newIndex = 0;
    }
    setCurrentIndex(newIndex);
    // Reset autoplay timer on manual navigation
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current);
      startAutoplayTimer();
    }
  };

  const startAutoplayTimer = () => {
    if (isAutoplay) {
      autoplayTimerRef.current = setTimeout(() => {
        goToSlide(currentIndex + 1);
      }, 6000);
    }
  };

  useEffect(() => {
    startAutoplayTimer();
    
    return () => {
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
      }
    };
  }, [currentIndex, isAutoplay]);

  return (
    <section className="hidden py-20 relative overflow-hidden" id="testimonials">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-tech-blue/5 to-transparent -z-10"></div>
      
      <Container>
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-block px-3 py-1 rounded-full bg-tech-cyan/10 text-tech-cyan font-medium text-sm mb-4 animate-fade-in opacity-0" style={{animationDelay: "0.1s", animationFillMode: "forwards"}}>
            Client Success Stories
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-tech-blue mb-6 animate-fade-in opacity-0" style={{animationDelay: "0.2s", animationFillMode: "forwards"}}>
            Hear From Our Clients
          </h2>
          
          <p className="text-lg text-gray-700 animate-fade-in opacity-0" style={{animationDelay: "0.3s", animationFillMode: "forwards"}}>
            Discover how our innovative solutions have transformed businesses and delivered measurable results.
          </p>
        </div>
        
        <div className="relative max-w-5xl mx-auto">
          {/* Testimonial Slider */}
          <div className="overflow-hidden">
            <div className="transition-all duration-700 ease-in-out" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
              <div className="flex">
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-4">
                    <div className="glass-panel rounded-2xl overflow-hidden shadow-xl">
                      <div className="p-8 md:p-10 flex flex-col md:flex-row items-center">
                        <div className="mb-6 md:mb-0 md:mr-8 md:w-1/4 flex-shrink-0">
                          <div className="relative w-24 h-24 md:w-28 md:h-28 mx-auto rounded-full overflow-hidden border-4 border-white shadow-md">
                            <img 
                              src={testimonial.image}
                              alt={testimonial.author} 
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        </div>
                        
                        <div className="md:w-3/4">
                          <div className="mb-4 text-tech-cyan">
                            <Quote className="w-12 h-12 opacity-20" />
                          </div>
                          
                          <p className="text-lg md:text-xl text-gray-800 italic mb-6">
                            "{testimonial.quote}"
                          </p>
                          
                          <div>
                            <h4 className="text-lg font-bold text-tech-blue">{testimonial.author}</h4>
                            <p className="text-gray-600 mb-4">{testimonial.role}</p>
                            
                            <div className="flex">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={cn(
                                    "w-5 h-5 mr-1",
                                    i < testimonial.rating ? "text-amber-400 fill-amber-400" : "text-gray-300"
                                  )} 
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Navigation Controls */}
          <div className="mt-8 flex items-center justify-center space-x-4">
            <button 
              onClick={() => goToSlide(currentIndex - 1)}
              className="bg-white p-3 rounded-full shadow-md hover:shadow-lg transition-all duration-300 text-tech-blue hover:text-tech-cyan"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={cn(
                    "w-3 h-3 rounded-full transition-all duration-300",
                    currentIndex === index 
                      ? "bg-tech-cyan w-8" 
                      : "bg-gray-300 hover:bg-gray-400"
                  )}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
            
            <button 
              onClick={() => goToSlide(currentIndex + 1)}
              className="bg-white p-3 rounded-full shadow-md hover:shadow-lg transition-all duration-300 text-tech-blue hover:text-tech-cyan"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Testimonials;
